
import { elem } from "./chrome";

const STYLE_ID = "intro-chrome";

export const INTRO_ART = "/ui";

export function parseEventSet(raw: string): string {
  return raw
    .replace(/\[DELAY value;\d+\]/g, "")
    .replace(/\[WAIT\]/g, "")
    .replace(/[\r\n]+/g, "")
    .replace(/\[ENTER\]/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export const STAGE_WIDTH = 1024;
export const STAGE_HEIGHT = 768;

/* The loading gauge, as `loadingwindow.py` lays it out: a `GageBoard` window
   holding `BackGage` under `FullGage`, both pinned at the same offset inside
   it, so the fill crops over an empty track rather than replacing it. */
export const LOADING_GAGEBOARD_WIDTH = 400; // loadingwindow.py:38
export const LOADING_GAGEBOARD_HEIGHT = 80; // loadingwindow.py:39
export const LOADING_GAGE_X = 40; // loadingwindow.py:48
export const LOADING_GAGE_Y = 25; // loadingwindow.py:49

export const INTRO_CSS = `
:root {
  --intro-gold: #e8c877;
  /* The same gold, lit -- what a gold control turns under the pointer.
     Stated once here rather than being written out on each screen that
     has such a control (.login-signin's hover was the first). */
  --intro-gold-bright: #f2d78d;
  --intro-gold-dim: #a89a72;
  --intro-ink: #d9d2c2;
  --intro-ink-bright: #e6e0d0;
  --intro-ink-quiet: #b3a98f;
  --intro-ink-dim: #8a8272;
  --intro-ink-faint: #6f6857;
  --intro-edge: #4a3f27;
  --intro-edge-soft: #3b3524;
  --intro-edge-strong: #6b5a2f;
  --intro-rule: #2b2719;
  --intro-panel-top: #16150f;
  --intro-panel-bottom: #0d0d0b;
  --intro-field: #0a0a08;
  --intro-button-top: #a98b45;
  --intro-button-bottom: #7d6531;
  --intro-button-ink: #12100a;
  --intro-danger: #b4553c;
  --intro-danger-ink: #e6a892;
  --intro-empire-shinsoo: #8d302f;
  --intro-empire-chunjo: #b59a45;
  --intro-empire-jinno: #3b638d;
  --intro-radius: 3px;

  /* The type scale, the spacing scale and the one control height.
   *
   * Stated once, here, because all three intro screens are one sequence to a
   * player and a scale that is redefined per screen is a scale in name only.
   * The login restyle picked these four steps and this 4px spacing rhythm
   * (style.css, .login), and .login's own --login-* tokens now alias these
   * rather than repeating the numbers -- so a step cannot be changed on one
   * screen and not on the next. */
  --intro-text-label: 0.6875rem; /* 11px -- uppercase, tracked */
  --intro-text-body: 0.8125rem; /*  13px -- helper text and refusals */
  --intro-text-control: 0.9375rem; /* 15px -- what you type in and press */
  --intro-text-title: 1.375rem; /*  22px -- the one heading on a screen */
  --intro-space-1: 4px;
  --intro-space-2: 8px;
  --intro-space-3: 12px;
  --intro-space-4: 18px;
  --intro-space-5: 26px;
  /* A touch target tools/e2e/intro.mjs measures at >= 40px on a phone. */
  --intro-control: 42px;

  /* The imported art. Named here so a screen references a token rather than a
     path, and so the whole set can be checked in one place against what
     tools/import_intro_ui.py actually wrote. */
  --intro-backdrop-login: url("${INTRO_ART}/login_bg.jpg");
  --intro-backdrop-select: url("${INTRO_ART}/select_bg.jpg");
  --intro-frame: url("${INTRO_ART}/thinboard.png");
  --intro-login-board: url("${INTRO_ART}/login_board.png");
  --intro-button: url("${INTRO_ART}/button_large.png");
  --intro-button-over: url("${INTRO_ART}/button_large_over.png");
  --intro-button-down: url("${INTRO_ART}/button_large_down.png");
  --intro-loading-gauge-empty: url("${INTRO_ART}/loading_gauge_empty.png");
  --intro-loading-gauge-full: url("${INTRO_ART}/loading_gauge_full.png");

  /* ui.ThinBoard.BOARD_COLOR, root/ui.py:1837. */
  --intro-board-fill: rgba(0, 0, 0, 0.51);
  /* ui.ThinBoard.CORNER_WIDTH / LINE_WIDTH, root/ui.py:1833-1836, and the
     slice tools/import_intro_ui.py packed thinboard.png at. */
  --intro-frame-slice: 16;
}

/* The letterbox. Black rather than a gradient, because the thing behind the
   art should read as absence and not as a second design. */
.intro-ground {
  position: fixed;
  inset: 0;
  display: grid;
  place-items: center;
  overflow: hidden;
  background: #05060a;
  color: var(--intro-ink);
  font: 13px/1.5 system-ui, -apple-system, "Segoe UI", sans-serif;
  pointer-events: auto;
}

/* display: grid above overrides the browser's presentation of the hidden
   attribute. Without an explicit rule, a dismissed select screen is still
   painted above character creation (its z-index is one higher), leaving the
   player looking at a disabled Play button while the usable form sits under
   it. Screen state and what a person sees must be the same state. */
.intro-ground[hidden] { display: none; }

/* The 1024x768 game window, at whatever size fits.
 *
 * 133.3333vh is 4/3 of the viewport height: the stage is as wide as the window
 * allows or as wide as its own height permits, whichever is smaller, and the
 * height mirrors it. That is the same arithmetic in both axes, so the box is
 * exactly 4:3 at every size and the backdrop inside it is never distorted.
 * Written this way rather than with aspect-ratio because the stage has to be
 * bounded on *both* axes -- a box that is only told its ratio and its width
 * grows off the bottom of a short window. */
.intro-stage {
  position: relative;
  width: min(100vw, 133.3333vh);
  height: min(75vw, 100vh);
  background-color: #05060a;
  background-repeat: no-repeat;
  background-position: center;
  background-size: 100% 100%;
  overflow: hidden;
}

.intro-ground-login .intro-stage { background-image: var(--intro-backdrop-login); }
.intro-ground-select .intro-stage { background-image: var(--intro-backdrop-select); }

/* The native client opens intrologo.LogoWindow, lets MovieMan render its
   logo AVI, and then changes phase. This pack does not ship that AVI, but it
   does ship the painted login image with the game's wordmark. Keep the two
   states visible rather than pretending a CSS animation is that movie. */
.intro-sequence { z-index: 1000; }
.intro-sequence .intro-stage { background-image: var(--intro-backdrop-login); }
.intro-sequence-shade {
  position: absolute;
  inset: 0;
  background: linear-gradient(rgba(0, 0, 0, 0.12), rgba(0, 0, 0, 0.66));
}
.intro-sequence-panel {
  position: absolute;
  left: 50%;
  bottom: 12%;
  width: min(54%, 440px);
  transform: translateX(-50%);
  color: var(--intro-ink-bright);
  text-align: center;
  text-shadow: 0 1px 2px #000;
}
.intro-sequence-logo .intro-sequence-panel { opacity: 0; }
.intro-sequence-status {
  margin: 0 0 7px;
  font-size: clamp(11px, 1.5vw, 14px);
  letter-spacing: 0.12em;
  text-transform: uppercase;
}
/* loadingwindow.py:45-61 draws gauge_empty under gauge_full; __SetProgress
   crops the fill with SetPercentage (introloading.py:208-210 / ui.py:877-882).
   Startup fetches still have no truthful byte total, so the fill sweeps rather
   than claiming a measured percent. */
.intro-sequence-gageboard {
  position: relative;
  width: min(${LOADING_GAGEBOARD_WIDTH}px, 88%);
  height: ${LOADING_GAGEBOARD_HEIGHT}px;
  margin: 0 auto;
}
/* The gauge starts at the board's own x offset and runs to the board's edge:
   the original places a 512px-wide raster at x=40 on a 400px board and lets
   the window clip the overhang (loadingwindow.py:36-52). */
.intro-sequence-gauge {
  position: absolute;
  left: ${LOADING_GAGE_X}px;
  top: ${LOADING_GAGE_Y}px;
  width: calc(100% - ${LOADING_GAGE_X}px);
  height: 20px;
  overflow: hidden;
  background: var(--intro-loading-gauge-empty) center / 100% 100% no-repeat;
}
.intro-sequence-gauge-fill {
  position: absolute;
  inset: 0;
  background: var(--intro-loading-gauge-full) left center / 100% 100% no-repeat;
  transform-origin: left center;
  animation: intro-loading-sweep 1.4s ease-in-out infinite;
}
@keyframes intro-loading-sweep {
  0% { clip-path: inset(0 100% 0 0); }
  50% { clip-path: inset(0 8% 0 0); }
  100% { clip-path: inset(0 100% 0 0); }
}

/* Furniture anchored the way the original anchors it: a horizontal centre and a
   fraction of the way down, both read off the uiscript.
 *
 * The column is placed by its *middle* rather than by its top. The original's
 * LoginBoard is 97 px tall and sits at y=358 of 768 (loginwindow.py:554), so
 * its centre is at 52.9%; the panels here carry controls the original has no
 * equivalent for -- registration, the demo accounts, the guest path -- and the
 * column is therefore taller than the board alone. Centring it on roughly the
 * board's own point keeps the form where the eye expects it instead of pushing
 * it off the bottom of the stage.
 *
 * 57% rather than 52.9% for one measured reason: at 52.9% the top of the column
 * lands on "The Dark Dragons", which is painted into login.jpg under the
 * wordmark. Four points lower clears it and still leaves the panel above the
 * publisher bar at the foot of the picture. Both of those are properties of the
 * painting rather than of the layout, which is why the number is not the
 * original's. */
.intro-anchor {
  position: absolute;
  left: 50%;
  top: 57%;
  transform: translate(-50%, -50%);
  width: min(92%, 560px);
  max-height: 94%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
}

/* Every panel on these screens is a ThinBoard: eight 16 px edge textures around
   a 51%-black interior (root/ui.py:1839-1873). CSS does that natively --
   border-image with a slice of 16 pins the four corners and repeats the four
   edges, which is precisely what ThinBoard.SetSize does by hand. fill is
   deliberately not given: the original does not put a texture in the middle
   either, it puts a flat colour there, and leaving the centre cell out lets the
   background-color show through. */
.intro-board {
  border: 16px solid transparent;
  border-image-source: var(--intro-frame);
  border-image-slice: var(--intro-frame-slice);
  border-image-repeat: repeat;
  background-color: var(--intro-board-fill);
  background-clip: padding-box;
  color: var(--intro-ink);
}

/* A board big enough that the painting behind it competes with the text on it.
 *
 * 51% black is the original's own figure and the right one for the boards the
 * original draws, which are small and sit over the dark middle of their own
 * backdrops. The select and create screens here put a 860 px panel over a lit
 * town street, and at 51% the subtitle on it is barely legible -- measured by
 * looking at tools/e2e/shots/intro-select.png, which is what that harness is
 * for. This lays a second flat 45% over the first, for about 73% in total. A
 * departure from the original's number, and deliberate: the number was chosen
 * for a board a fifth of this size. */
.intro-board-dense {
  background-image: linear-gradient(rgba(0, 0, 0, 0.45), rgba(0, 0, 0, 0.45));
}

.intro-title {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: var(--intro-gold);
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.9);
}

.intro-subtitle {
  margin: 3px 0 0;
  font-size: 0.76rem;
  color: var(--intro-ink-quiet);
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.9);
}

/* Every label on these screens: small, spaced and upper case, which is how the
   original's Parameter_Slot rows read even though it gets there with a texture
   rather than with letter-spacing. */
.intro-label {
  font-size: 0.68rem;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--intro-ink-dim);
}

/* A box you type into, matching the recessed black rectangles baked into
   login_board.png so a field outside the board frame does not read as a
   different kind of control from one inside it. */
.intro-field {
  padding: 6px 9px;
  border: 1px solid #000;
  border-radius: 0;
  background: var(--intro-field);
  color: var(--intro-ink-bright);
  font: inherit;
  font-size: 0.9rem;
}

.intro-field:focus {
  outline: none;
  border-color: var(--intro-button-top);
  box-shadow: 0 0 0 1px rgba(169, 139, 69, 0.5);
}

/* The button, which is d:/ymir work/ui/public/large_button_01..03.sub and not
 * a gradient any more.
 *
 * A *horizontal* 3-slice, and the horizontal part is the whole point. The
 * sprite is 88x21 with a rounded stone cap at each end, so the caps have to be
 * pinned or a long label smears them -- but slicing the vertical axis as well
 * cannot work at this size: a 12 px slice off the top and another off the
 * bottom is 24 px of a 21 px sprite, the two overlap, the middle row collapses
 * and the button renders as two thin bars with a gap between them. (It did.
 * That is what the first run of tools/e2e/intro.mjs photographed.) So the
 * slice is 0 top and bottom and 12 left and right: the caps are pinned, the
 * middle is stretched both ways, and stretching it vertically is harmless
 * because the sprite is a plain vertical gradient with no detail to smear.
 *
 * _02 is the over state and _03 the down state, in the original's own
 * naming: loginwindow.py:604-606 binds default/over/down in that order. */
.intro-button {
  min-height: 30px;
  padding: 5px 8px;
  border: 0 solid transparent;
  border-left-width: 12px;
  border-right-width: 12px;
  border-image-source: var(--intro-button);
  border-image-slice: 0 12 fill;
  border-image-width: 0 12px;
  border-image-repeat: stretch;
  background: transparent;
  color: var(--intro-ink-bright);
  font: inherit;
  font-size: 0.86rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.85);
  cursor: pointer;
}

.intro-button:hover:not(:disabled) {
  border-image-source: var(--intro-button-over);
  color: var(--intro-gold);
}

.intro-button:active:not(:disabled) {
  border-image-source: var(--intro-button-down);
}

.intro-button:disabled {
  opacity: 0.45;
  cursor: default;
}

/* The one action on a screen that is meant to be pressed next reads gold
   rather than bone, which is the distinction the flat palette used to make
   with a filled button against an outlined one. */
.intro-button-primary {
  color: var(--intro-gold);
}

.intro-button-quiet {
  font-weight: 400;
  color: var(--intro-ink-quiet);
}

/* Destructive, and said with a colour rather than with a bigger button. */
.intro-button-danger {
  font-weight: 400;
  color: var(--intro-danger-ink);
}

.intro-button-danger:hover:not(:disabled) {
  color: var(--intro-danger-ink);
  filter: saturate(1.4) brightness(1.15);
}

.intro-error {
  margin: 0;
  padding: 6px 9px;
  border-left: 2px solid var(--intro-danger);
  background: rgba(60, 12, 6, 0.75);
  color: var(--intro-danger-ink);
  font-size: 0.8rem;
}

.intro-rule {
  border: 0;
  border-top: 1px solid var(--intro-rule);
  margin: 12px 0;
}

/* On a narrow screen -- narrow in pixels or in aspect ratio -- the 4:3 stage is
   too short to hold a browser-sized form. The in-app browser is commonly about
   690x900: wider than a phone breakpoint but portrait enough that a 4:3 stage
   is only 518px tall and clips the guest controls. Abandon the letterbox in
   both cases; the backdrop crops while the furniture keeps the full viewport. */
@media (max-width: 560px), (max-aspect-ratio: 4 / 3) {
  .intro-stage {
    width: 100vw;
    height: 100vh;
    background-size: cover;
  }
  .intro-anchor {
    width: min(96%, 420px);
    top: 50%;
  }
}
`;

export function installIntroStyle(doc: Document): void {
  if (doc.getElementById(STYLE_ID) !== null) return;
  const style = doc.createElement("style");
  style.id = STYLE_ID;
  style.textContent = INTRO_CSS;
  doc.head.append(style);
}

export function introGround(
  doc: Document,
  backdrop: "login" | "select",
): { ground: HTMLElement; anchor: HTMLElement } {
  const ground = elem(doc, "div", `intro-ground intro-ground-${backdrop}`);
  const stage = elem(doc, "div", "intro-stage");
  const anchor = elem(doc, "div", "intro-anchor");
  stage.append(anchor);
  ground.append(stage);
  return { ground, anchor };
}

export class IntroSequence {
  readonly element: HTMLElement;
  private readonly status: HTMLElement;

  constructor(doc: Document, mount: HTMLElement = doc.body) {
    installIntroStyle(doc);
    const { ground } = introGround(doc, "login");
    ground.classList.add("intro-sequence", "intro-sequence-logo");
    ground.dataset.introPhase = "logo";
    const stage = ground.querySelector<HTMLElement>(".intro-stage");
    if (stage === null) throw new Error("intro ground is missing its stage");
    const shade = elem(doc, "div", "intro-sequence-shade");
    const panel = elem(doc, "div", "intro-sequence-panel");
    const status = elem(doc, "p", "intro-sequence-status", "Loading");
    const gageboard = elem(doc, "div", "intro-sequence-gageboard");
    const gauge = elem(doc, "div", "intro-sequence-gauge");
    gauge.setAttribute("role", "progressbar");
    gauge.setAttribute("aria-label", "Loading game data");
    gauge.setAttribute("aria-valuetext", "Loading");
    const fill = elem(doc, "div", "intro-sequence-gauge-fill");
    fill.setAttribute("aria-hidden", "true");
    gauge.append(fill);
    gageboard.append(gauge);
    panel.append(status, gageboard);
    stage.append(shade, panel);
    mount.append(ground);
    this.element = ground;
    this.status = status;
  }

  loading(status = "Loading game data"): void {
    this.element.classList.remove("intro-sequence-logo");
    this.element.dataset.introPhase = "loading";
    this.status.textContent = status;
    this.element.querySelector(".intro-sequence-gauge")?.setAttribute("aria-valuetext", status);
  }

  close(): void {
    this.element.dataset.introPhase = "closed";
    this.element.remove();
  }
}
